import { getToken, clearToken } from '../utils/authToken';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LanguageContext = createContext();

// Storage key for localStorage (fallback de hidratacion)
const LANGUAGE_STORAGE_KEY = 'damyxia-language';
// Cookie firmada del backend (cross-device, fuente de verdad para no logueados)
const LANGUAGE_COOKIE_NAME = 'damyxia_lang';
const DEFAULT_LANGUAGE = 'es';
const SUPPORTED_LANGUAGES = ['es', 'en'];

// Backend API URL
const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Lee el valor de una cookie del documento. Devuelve null si no existe.
 */
const readCookie = (name) => {
  try {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(name + '='));
    if (!match) return null;
    const value = match.split('=')[1];
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

/**
 * Persiste la preferencia de idioma en una cookie firmada del backend.
 * Funciona para usuarios anonimos (no requiere token).
 */
const syncLanguageCookie = async (lang) => {
  try {
    await fetch(`${API_URL}/language/set`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lang }),
    });
  } catch (err) {
    // Silently fail: localStorage sigue siendo el fallback local.
    console.warn('Failed to sync language cookie:', err);
  }
};

/**
 * Detects browser language and returns 'en' if English, otherwise 'es' (default)
 * Checks navigator.language and navigator.languages
 */
const detectBrowserLanguage = () => {
  try {
    // Get browser language(s)
    const browserLang = navigator.language || navigator.userLanguage || '';
    const browserLangs = navigator.languages || [browserLang];
    
    // Check if any preferred language starts with 'en'
    for (const lang of browserLangs) {
      const langCode = lang.toLowerCase().split('-')[0]; // 'en-US' -> 'en'
      if (langCode === 'en') {
        return 'en';
      }
      if (langCode === 'es') {
        return 'es';
      }
    }
    
    // Default to Spanish if no match
    return DEFAULT_LANGUAGE;
  } catch (error) {
    return DEFAULT_LANGUAGE;
  }
};

export const translations = {
  es: {
    // Welcome Screen
    welcome: {
      title: 'Tu IA que construye proyectos reales',
      // 🟥 Cuadro Negro 2026-05-30 — claves para el efecto de título intercalado.
      titleAlt1: 'Tu IA que construye proyectos reales',
      titleAlt2: 'DamyxIA',
      subtitle: 'Tu asistente de inteligencia artificial',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      installApp: 'Instalar DamyxIA',
      appInstalled: '¡App instalada!',
      poweredBy: 'Potenciado por IA avanzada',
      userRights: 'Derechos del Usuario',
      forWhom: 'Para Quién es DamyxIA',
      forWhomTitle: 'Visionarios sin código',
      forWhomDesc: 'Personas con grandes ideas que no deberían verse limitadas por la falta de conocimientos técnicos o económicos.',
      forWhomFeature1: 'Interfaz intuitiva en español e inglés',
      forWhomFeature2: 'Sin curva de aprendizaje ni límites de las ideas',
    },
    // 🟥 Home page (/home) — pitch oficial DamyxIA.
    home: {
      title: 'Tu IA que construye proyectos reales',
      taglineLine1: 'Vos escribís lo que querés.',
      taglineLine2: 'DamyxIA construye el proyecto entero.',
      intro1: 'DamyxIA no es un chatbot.',
      intro2: 'Es un constructor de proyectos completo, capaz de generar aplicaciones web, APIs, CLIs, apps móviles, monorepos y sistemas híbridos listos para ejecutar.',
      intro3: 'Vos escribís lo que querés. DamyxIA construye el proyecto entero.',
      howItWorks: {
        heading: 'Cómo funciona',
        s1Title: '1. Entendé tu idea',
        s1Body: 'Escribí en lenguaje natural lo que querés construir. DamyxIA analiza tu intención, detecta frameworks, estructura y componentes necesarios.',
        s2Title: '2. Genera el proyecto completo',
        s2Body: 'Produce código real, organizado, con carpetas, módulos, dependencias y archivos listos para ejecutar.',
        s3Title: '3. Renderiza o activa el Modo Alternativo',
        s3Items: [
          'Si el proyecto es web → lo ves renderizado en vivo.',
          'Si es backend, CLI, mobile o monorepo → DamyxIA activa el Modo Alternativo, explicando qué tipo de proyecto es y cómo usarlo.',
        ],
        s4Title: '4. Explorá los archivos generados',
        s4Intro: 'Abrí el Explorador de Archivos:',
        s4Items: [
          'Árbol real del proyecto',
          'Carpetas primero, archivos después',
          'Tamaño por archivo (KB/MB)',
          'Total de archivos y peso total',
          'Vista limpia, profesional, bilingüe',
          '100% read-only',
        ],
        s5Title: '5. Descargá el ZIP profesional',
        s5Intro: 'Cada descarga incluye un README inteligente, con:',
        s5Items: [
          'Nombre del proyecto',
          'Fecha de generación',
          'Frameworks detectados',
          'Rutas recomendadas (cd frontend, cd backend, etc.)',
          'Instrucciones reales de instalación y ejecución',
          'Árbol de carpetas inteligente',
          'Contador de archivos truncados',
          'Footer "Powered by DamyxIA"',
        ],
        s5Note: 'Si el proyecto ya tenía README → no se sobrescribe.',
      },
      canGenerate: {
        heading: 'Qué puede generar DamyxIA',
        frontendTitle: 'Frontend',
        frontendItems: ['Next.js', 'Vite + React', 'Astro', 'Vanilla web apps'],
        backendTitle: 'Backend',
        backendItems: ['Express', 'Fastify', 'Koa', 'Hono', 'Node.js puro'],
        mobileTitle: 'Mobile',
        mobileItems: ['Expo', 'React Native'],
        cliTitle: 'CLI Tools',
        cliItems: ['Node CLI', 'Scripts utilitarios'],
        monoTitle: 'Monorepos híbridos',
        monoItems: [
          'Next.js + Express',
          'Vite + Fastify',
          'Expo + API Node',
          'Web + CLI',
          'Cualquier combinación múltiple',
        ],
      },
      altMode: {
        heading: 'Modo Alternativo (cuando no se puede renderizar)',
        intro: 'Si el proyecto no es web (backend, CLI, mobile, monorepo), DamyxIA activa un panel especial:',
        items: [
          'Explica el tipo de proyecto',
          'Muestra cómo ejecutarlo',
          'Permite ver los archivos generados',
          'Permite descargar el ZIP profesional',
        ],
        outro: 'Sin errores. Sin confusión. Sin ruido técnico.',
      },
      aiTemplates: {
        heading: 'Plantillas IA — Crea proyectos completos en segundos',
        description: 'Las Plantillas IA son modelos listos para usar que te permiten generar dashboards, landings, funnels, portfolios y más con un solo clic. Solo elige una plantilla y envíala a tu Workspace para verla visualmente y editarla como quieras.',
        accessTitle: 'Acceso',
        accessFree: 'Usuarios Free: pueden usar todas las plantillas gratuitas.',
        accessPremium: 'Usuarios Premium: acceso completo a todas las plantillas, incluidas las avanzadas y de marketing IA.',
        howToTitle: 'Cómo usarlas',
        howToItems: [
          'Abre el panel de Plantillas desde el Workspace.',
          'Elige una plantilla.',
          'Pulsa "Enviar al Workspace".',
          'La plantilla se abrirá visualmente lista para editar.',
        ],
        premiumTitle: 'Plantillas Premium',
        premiumNote: 'Algunas plantillas avanzadas están marcadas como "PREMIUM" y requieren una suscripción activa.',
      },
      explorer: {
        heading: 'Explorador de Archivos (Drawer)',
        intro: 'Un panel lateral profesional con:',
        items: [
          'Árbol real del proyecto',
          'Tamaño por archivo',
          'Total de archivos y peso total',
          'Cierre con ESC o clic afuera',
          'Bilingüe ES/EN',
          '100% read-only',
        ],
        outro: 'Es como mirar el proyecto desde VSCode, pero dentro de DamyxIA.',
      },
      zip: {
        heading: 'ZIP profesional con README inteligente',
        intro: 'Cada ZIP incluye:',
        items: [
          'README bilingüe',
          'Metadatos (nombre + fecha)',
          'Frameworks detectados',
          'Rutas recomendadas',
          'Instrucciones reales',
          'Árbol inteligente',
          'Contador de archivos truncados',
          'Footer "Powered by DamyxIA"',
        ],
      },
      bilingual: {
        heading: 'Bilingüe automático',
        intro: 'DamyxIA detecta tu idioma (ES/EN) y adapta:',
        items: ['Mensajes', 'Paneles', 'README', 'Árbol truncado', 'Footer', 'Secciones híbridas'],
      },
      stability: {
        heading: 'Estabilidad y seguridad',
        items: [
          'No toca el motor de IA',
          'No toca el Orquestador',
          'No toca el Auto-Builder',
          'No toca el Workspace principal',
          'No ejecuta código',
          'No modifica proyectos existentes',
        ],
      },
      status: {
        heading: 'Estado actual del sistema',
        intro: 'DamyxIA está en su mejor estado histórico:',
        items: [
          'Más de 100 tests automáticos garantizan estabilidad',
          'Cero errores de consola',
          'Modo Alternativo completo',
          'README híbrido perfecto',
          'Árbol inteligente',
          'Drawer profesional',
          'STANDBY activo (REGISTRATION_OPEN=false)',
        ],
      },
      cta: {
        heading: 'Empezá a construir',
        line1: 'Pedile a DamyxIA lo que quieras crear.',
        line2: 'El proyecto completo aparece en segundos.',
      },
    },
    // 🟥 GitHub integration (Premium-only — Cuadro Negro 2026-05-30)
    github: {
      saveToGitHub: 'Guardar en GitHub',
      connectGitHub: 'Conectar con GitHub',
      repoCreated: 'Repositorio creado',
      saveError: 'Error al guardar en GitHub',
    },
    // Features
    features: {
      codeGen: 'Generación de Código',
      codeGenDesc: 'Crea código en cualquier lenguaje',
      imageGen: 'Creación de Imágenes',
      imageGenDesc: 'Genera imágenes con IA',
      diagrams: 'Diagramas',
      diagramsDesc: 'Crea diagramas visuales',
      memory: 'Memoria Contextual',
      memoryDesc: 'Recuerda tus preferencias',
      github: 'GitHub',
      githubDesc: 'Integración completa',
    },
    // Terms Modal
    terms: {
      title: 'Términos y Condiciones',
      intellectualProperty: 'Su Propiedad Intelectual',
      intellectualPropertyText: 'Todas las aplicaciones, proyectos o contenido generado a través de nuestra plataforma <strong>DamyxIA</strong> son considerados <strong class="text-green-400">de su propiedad</strong>. No asumimos ningún derecho de propiedad sobre nada que usted cree aquí dentro de DamyxIA.',
      totalFreedom: 'Libertad Total',
      totalFreedomText: 'Sus ideas y propiedad intelectual continuarán siendo <strong>solo suyas</strong>. Tiene la libertad de <strong class="text-green-400">usar, modificar, distribuir o vender</strong> las aplicaciones generadas según lo permita la ley.',
      yourResponsibility: 'Su Responsabilidad',
      yourResponsibilityText: 'Usted es el <strong>único responsable</strong> de sus actos o acciones. También es su responsabilidad guardar sus trabajos o proyectos. No asumimos responsabilidad alguna por pérdida de datos debido a no guardar su trabajo.',
      damyxiaRights: 'Derechos de DamyxIA',
      damyxiaRightsText1: 'DamyxIA se reserva el derecho de introducir en cualquier momento <strong>mejoras o cambios</strong> de cualquier acuerdo existente con el usuario.',
      damyxiaRightsText2: 'DamyxIA puede <strong class="text-red-400">cesar sus servicios sin previo aviso</strong> por el tiempo necesario, incluso por tiempo indeterminado, donde todo pudiera dejar de existir o de funcionar.',
      damyxiaRightsText3: '<strong class="text-green-400">Nunca utilizaremos</strong> aplicaciones o proyectos de los usuarios con ningún fin, ni personal ni de algún otro propósito.',
      damyxiaRightsText4: '⚠️ Es responsabilidad exclusiva del usuario guardar y respaldar fuera de DamyxIA todos sus trabajos y aplicaciones, para evitar cualquier situación futura desagradable.',
      disclaimer: 'Al utilizar DamyxIA, usted acepta estos términos y condiciones.',
      understood: 'Entendido',
    },
    // Auth
    auth: {
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      email: 'Correo electrónico',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      forgotPassword: '¿Olvidaste tu contraseña?',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      registerHere: 'Regístrate aquí',
      loginHere: 'Inicia sesión aquí',
      loggingIn: 'Iniciando sesión...',
      registering: 'Registrando...',
      backToLogin: 'Volver al inicio de sesión',
      passwordsNotMatch: 'Las contraseñas no coinciden',
      passwordMinLength: 'La contraseña debe tener al menos 6 caracteres',
    },
    // Forgot Password
    forgotPwd: {
      title: 'Recuperar Contraseña',
      resetTitle: 'Restablecer Contraseña',
      subtitle: 'Ingresa tu correo para recibir un enlace de recuperación',
      checkEmail: 'Revisa tu correo electrónico',
      createNew: 'Crea una nueva contraseña',
      done: '¡Listo! Tu contraseña ha sido actualizada',
      sendLink: 'Enviar enlace de recuperación',
      sending: 'Enviando...',
      newPassword: 'Nueva contraseña',
      confirmNewPassword: 'Confirmar contraseña',
      resetting: 'Restableciendo...',
      resetPassword: 'Restablecer contraseña',
      successReset: 'Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión con tu nueva contraseña.',
      goToLogin: 'Ir a Iniciar Sesión',
      linkReady: '¡Listo! Usa este enlace:',
      resetMyPassword: '🔐 Restablecer Mi Contraseña',
      copy: 'Copiar',
      linkCopied: '¡Enlace copiado!',
      alsoSentTo: 'También enviamos el enlace a',
      checkSpam: '(Revisa spam si no lo ves)',
      tryOtherEmail: '← Intentar con otro correo',
    },
    // Dashboard
    dashboard: {
      newChat: 'Nueva conversación',
      conversations: 'Conversaciones',
      noConversations: 'No hay conversaciones',
      workspace: 'Área de Trabajo',
      internalWorkspace: 'Área Interna de Trabajo',
      workspaceTitle: 'Área de Trabajo',
      workspaceSubtitle: 'Panel Derecho • Vista en Vivo y Ejecución',
      workspaceEmpty: 'Área de trabajo vacía',
      workspaceEmptyDesc: 'Selecciona o crea una conversación para ver el trabajo ejecutado',
      noCodeOrDiagrams: 'Sin código ni diagramas',
      noCodeOrDiagramsDesc: 'Pide a DamyxIA que genere código o diagramas',
      poweredBy: 'Por DamyxIA',
      damyxiaChoice: 'DamyxIA es tu Opción',
      dragImageHint: 'Arrastra una imagen aquí o escribe un mensaje',
      livePreview: 'Vista Previa en Vivo',
      previewArea: 'Área de Vista Previa',
      previewAreaDesc: 'Aquí aparecerá el resultado de lo que DamyxIA genere. Escribe algo en el panel izquierdo para comenzar.',
      rightPanel: 'Panel Derecho',
      liveExecution: 'Vista Previa y Ejecución',
      synced: 'Sincronizado',
      updated: 'Actualizado',
      visualInspection: 'Inspección Visual',
      generatedCode: 'Código Generado',
      footer: 'Panel Derecho • Vista Previa y Ejecución • DamyxIA',
      waiting: 'Esperando',
      generating: 'Generando...',
      generated: 'Generado',
      repaired: 'Reparado',
      error: 'Error',
      refreshView: 'Refrescar vista',
      viewCode: 'Ver código',
      expand: 'Expandir',
      minimize: 'Minimizar',
      review: 'Revisar',
      chat: 'Chat',
      logout: 'Salir',
      profile: 'Perfil',
      settings: 'Configuración',
      connected: 'Conectado',
      diagrams: 'diagrama(s)',
      codeBlocks: 'bloque(s) de código',
      copied: 'Copiado',
      download: 'Descargar',
      downloadSvg: 'Descargar SVG',
      diagramDownloaded: 'Diagrama descargado',
      run: 'Ejecutar',
      reviewProject: 'Revisar Proyecto',
      fixesApplied: 'Correcciones aplicadas',
      flowchart: 'Diagrama de Flujo',
      sequence: 'Diagrama de Secuencia',
      classDiagram: 'Diagrama de Clases',
      stateDiagram: 'Diagrama de Estados',
      erDiagram: 'Diagrama ER',
      gantt: 'Diagrama de Gantt',
      pie: 'Gráfico Circular',
      gitGraph: 'Git Graph',
      mindmap: 'Mapa Mental',
      timeline: 'Línea de Tiempo',
      diagram: 'Diagrama',
    },
    // Gallery
    gallery: {
      title: 'Galería',
      communityGallery: 'Galería de la Comunidad',
      shareDesign: 'Compartir Diseño',
      likes: 'Me gusta',
      noDesigns: 'No hay diseños',
      myApps: 'Mis Apps',
      publicGallery: 'Apps Públicas',
      publish: 'Publicar',
      clone: 'Clonar',
      edit: 'Editar',
      delete: 'Eliminar',
      views: 'Vistas',
      clones: 'Clones',
      published: 'Publicada',
      draft: 'Borrador',
      archived: 'Archivada',
      makePublic: 'Hacer pública',
      makePrivate: 'Hacer privada',
      publishApp: 'Publicar mi App',
      appName: 'Nombre de la App',
      description: 'Descripción',
      tags: 'Etiquetas',
      public: 'Pública',
      private: 'Privada',
      publishSuccess: '¡App publicada exitosamente!',
      cloneSuccess: '¡App clonada!',
      noApps: 'No hay apps',
      createFirst: 'Crea tu primera app con DamyxIA',
      discoverApps: 'Descubre apps creadas por la comunidad',
      loadMore: 'Cargar más',
      recent: 'Recientes',
      popular: 'Populares',
      mostViewed: 'Más vistas',
      allTags: 'Todas',
      searchApps: 'Buscar apps...',
    },
    // Floating Preview
    floatingPreview: {
      title: 'Editor de Dashboard',
      code: 'Código',
      editCode: 'Editar Código',
      apply: 'Aplicar',
      discard: 'Descartar cambios',
      reload: 'Recargar',
      openFloating: 'Abrir en ventana flotante',
      maximize: 'Maximizar',
      restore: 'Restaurar',
    },
    // Chat
    chat: {
      placeholder: 'Escribe tu mensaje a DamyxIA...',
      placeholderFile: 'Escribe una pregunta sobre el archivo...',
      send: 'Enviar',
      thinking: 'Pensando...',
      attachFile: 'Adjuntar archivo',
      generateImage: 'Generar imagen',
      generateDiagram: 'Generar diagrama',
      copy: 'Copiar',
      copied: '¡Copiado!',
      download: 'Descargar',
      analyzeFile: 'Analiza este archivo',
      supportedFormats: 'Soporta: Código, PDFs, Imágenes • Genera imágenes con IA',
      fileTypeNotSupported: 'Tipo de archivo no soportado. Extensiones permitidas:',
      fileTooLarge: 'El archivo es demasiado grande. Máximo 10MB.',
      newConversation: 'Nueva Conversación',
      welcome: 'Bienvenido a DamyxIA',
      welcomeDesc: 'Tu asistente de inteligencia artificial. ¿En qué puedo ayudarte hoy?',
      suggestions: {
        code: 'Genera código',
        image: 'Crea una imagen',
        diagram: 'Haz un diagrama',
        help: 'Ayúdame con...',
      },
      rollback: 'Revertir',
    },
    // App Publishing
    appPublish: {
      published: '¡App publicada!',
      publishedUrl: '¡App publicada! URL:',
      errorPublishing: 'Error al publicar app',
      notPublished: 'Esta imagen aún no está publicada. Usa el chat para publicarla.',
    },
    // Image Generator
    imageGen: {
      title: 'Generar Imagen',
      prompt: 'Describe la imagen que quieres crear',
      promptPlaceholder: 'Ej: Un atardecer en la playa con palmeras...',
      size: 'Tamaño',
      style: 'Estilo',
      generate: 'Generar Imagen',
      generating: 'Generando...',
      styles: {
        vivid: 'Vívido',
        natural: 'Natural',
      },
      actions: {
        copy: 'Copiar',
        download: 'Descargar',
        analyze: 'Analizar',
        variation: 'Variación',
      },
    },
    // Diagram Generator
    diagramGen: {
      title: 'Generar Diagrama',
      description: 'Describe el diagrama',
      descPlaceholder: 'Ej: Flujo de autenticación de usuario...',
      type: 'Tipo de diagrama',
      generate: 'Generar Diagrama',
      generating: 'Generando...',
      types: {
        flowchart: 'Diagrama de Flujo',
        sequence: 'Diagrama de Secuencia',
        class: 'Diagrama de Clases',
        er: 'Entidad-Relación',
        state: 'Diagrama de Estados',
        pie: 'Gráfico Circular',
        gantt: 'Diagrama de Gantt',
      },
    },
    // Memory Profile
    memory: {
      title: 'Memoria Contextual',
      profileTitle: 'Perfil de Usuario',
      profilePlaceholder: 'Describe quién eres, tu rol, preferencias...',
      contextTitle: 'Contexto Global',
      contextPlaceholder: 'Información que la IA debe recordar siempre...',
      save: 'Guardar',
      saving: 'Guardando...',
      saved: '¡Guardado!',
      cancel: 'Cancelar',
    },
    // GitHub
    github: {
      title: 'Conectar con GitHub',
      tokenLabel: 'Personal Access Token',
      tokenPlaceholder: 'ghp_xxxxxxxxxxxx',
      tokenHelp: 'Genera un token en GitHub > Settings > Developer settings > Personal access tokens',
      connect: 'Conectar',
      connecting: 'Conectando...',
      connected: 'Conectado',
      disconnect: 'Desconectar',
      repositories: 'Repositorios',
      noRepos: 'No se encontraron repositorios',
    },
    // Settings Modal
    settings: {
      title: 'Configuración',
      subtitle: 'Gestiona cómo recibes las notificaciones por correo electrónico',
      projectUpdates: 'Actualizaciones de proyectos',
      projectUpdatesDesc: 'Recibe notificaciones cuando tus proyectos se actualicen o finalicen',
      newFeatures: 'Nuevas funciones',
      newFeaturesDesc: 'Sé el primero en enterarte de las nuevas funciones y mejoras',
      communityHighlights: 'Lo más destacado de la comunidad',
      communityHighlightsDesc: 'Resumen semanal de los proyectos más populares de la comunidad',
      securityAlerts: 'Alertas de seguridad',
      securityAlertsDesc: 'Notificaciones de seguridad importantes sobre tu cuenta',
      marketing: 'Marketing y promociones',
      marketingDesc: 'Ofertas especiales, consejos y contenido promocional',
      savePreferences: 'Guardar preferencias',
      saved: '¡Preferencias guardadas!',
    },
    // Logout Modal
    logout: {
      title: 'Cerrar Sesión',
      message: '¿Estás seguro de que deseas cerrar sesión?',
      saveReminder: 'Asegúrate de haber guardado tu trabajo antes de salir.',
      confirm: 'Sí, cerrar sesión',
      cancel: 'Cancelar',
      saveAndExit: 'Guardar y Salir',
      exitWithoutSave: 'Salir sin Guardar',
    },
    // User Profile
    userProfile: {
      title: 'Mi Perfil',
      changePhoto: 'Cambiar foto',
      changePassword: 'Cambiar contraseña',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      save: 'Guardar cambios',
      saving: 'Guardando...',
    },
    // Common
    common: {
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      cancel: 'Cancelar',
      save: 'Guardar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      confirm: 'Confirmar',
      back: 'Volver',
      next: 'Siguiente',
      language: 'Idioma',
      copyError: 'No se pudo copiar',
      downloadError: 'Error al descargar',
      // Share
      shareTwitter: 'Compartir en Twitter',
      shareFacebook: 'Compartir en Facebook',
      shareLinkedIn: 'Compartir en LinkedIn',
      shareWhatsApp: 'Compartir en WhatsApp',
      shareEmail: 'Compartir por Email',
      // Likes
      like: 'Me gusta',
      unlike: 'Quitar me gusta',
      // Processing
      processing: 'Procesando...',
      pleaseWait: 'Por favor espera...',
      // Misc
      copied: 'Copiado',
      copy: 'Copiar',
      download: 'Descargar',
      refresh: 'Actualizar',
      search: 'Buscar',
      noResults: 'Sin resultados',
      seeMore: 'Ver más',
      seeLess: 'Ver menos',
      selectAll: 'Seleccionar todo',
      deselectAll: 'Deseleccionar todo',
      yes: 'Sí',
      no: 'No',
      or: 'o',
      and: 'y',
      from: 'de',
      to: 'a',
      at: 'a las',
      by: 'por',
    },
    // Internal Workspace
    internalWorkspace: {
      title: 'Área Interna de Trabajo',
      subtitle: 'Trabajos creados por DamyxIA',
      newWork: 'Nuevo Trabajo',
      search: 'Buscar trabajos...',
      empty: 'No hay trabajos internos',
      emptyHint: 'Crea tu primer trabajo usando el botón de arriba',
      loading: 'Cargando trabajos...',
      deleteConfirm: '¿Eliminar este trabajo?',
      deleted: 'Trabajo eliminado',
      created: 'Trabajo creado',
      updated: 'Trabajo actualizado',
      error: 'Error al procesar la solicitud',
      // New Module Form
      createModule: 'Crear Módulo',
      moduleName: 'Nombre del módulo',
      moduleDescription: 'Descripción (opcional)',
      moduleType: 'Tipo',
      moduleContent: 'Contenido del código',
      cancel: 'Cancelar',
      create: 'Crear',
      nameRequired: 'El nombre es obligatorio',
      // Module Card
      delete: 'Eliminar',
      doubleClickToEdit: 'Doble clic para editar',
      noDescription: '(Sin descripción)',
      copyFailed: 'Error al copiar',
      codeSaved: 'Código guardado',
      saveFailed: 'Error al guardar',
      pdfDownloaded: 'PDF descargado',
      pdfExportFailed: 'Error al exportar PDF',
      // Module Details
      preview: 'Vista Previa',
      code: 'Código',
      copy: 'Copiar',
      save: 'Guardar',
      exportPdf: 'Exportar PDF',
      editInChat: 'Editar en chat',
      close: 'Cerrar',
      noModuleSelected: 'Selecciona un módulo para ver más detalles',
      // Types
      component: 'Componente',
      page: 'Página',
      utility: 'Utilidad',
      style: 'Estilo',
      config: 'Configuración',
    },
    // Workspace (Dashboard)
    workspace: {
      title: 'Área de Trabajo',
      expand: 'Expandir',
      collapse: 'Contraer',
      items: 'elemento(s)',
      images: 'Imágenes',
      versionRestored: 'Versión restaurada',
      failedToLoad: 'Error al cargar imagen',
      noImageSelected: 'Ninguna imagen seleccionada',
      emptyWorkspace: 'Espacio de trabajo vacío',
      emptyHint: 'Sube una imagen al chat para comenzar',
      rollback: 'Revertir',
      history: 'Historial',
      versions: 'Versiones',
      latestVersion: 'Última versión',
    },
    // Image Workspace
    imageWorkspace: {
      save: 'Guardar',
      download: 'Descargar',
      print: 'Imprimir',
      edit: 'Editar',
      delete: 'Eliminar',
      variants: 'Variantes',
      original: 'Original',
      images: 'Imágenes',
      closeImage: 'Cerrar imagen',
      variant: 'Variante',
      noVariants: 'Sin variantes',
      createVariantHint: 'Usa el editor para crear variantes',
      saved: 'Imagen guardada',
      downloaded: 'Imagen descargada',
      deleted: 'Elemento eliminado',
      errorSaving: 'Error al guardar',
      errorDownloading: 'Error al descargar',
      errorDeleting: 'Error al eliminar',
      confirmDelete: '¿Eliminar este elemento?',
      zoomIn: 'Acercar',
      zoomOut: 'Alejar',
      fitToScreen: 'Ajustar',
      imageSource: 'Imagen Original',
      imageVariant: 'Variante',
      noImageSelected: 'No hay imagen seleccionada',
      dropImageHere: 'Suelta la imagen aquí',
      willAppearInChat: 'Se mostrará en la conversación',
      processing: 'Procesando...',
      dragOrTypeMessage: 'Arrastra una imagen aquí o escribe un mensaje',
      // Additional
      popupBlocked: 'Popup bloqueado',
      noImageToDelete: 'No hay imagen para borrar',
      confirmDeleteImage: '¿Estás seguro de que quieres eliminar esta imagen?',
      imageDeleted: 'Imagen eliminada',
      errorDeleted: 'Error al eliminar',
      back: 'Volver',
      doubleClickToEdit: 'Doble clic para editar',
    },
    // Image Gallery Modal
    imageGallery: {
      title: 'Galería de Imágenes',
      allImages: 'Todas',
      originals: 'Originales',
      variants: 'Variantes',
      pending: 'Pendientes',
      noImages: 'No hay imágenes',
      noImagesHint: 'Sube una imagen al chat para comenzar',
      search: 'Buscar imágenes...',
      loadingImages: 'Cargando imágenes...',
      imageSource: 'Original',
      imageVariant: 'Variante',
      imageReady: 'Lista para trabajar',
      selectToEdit: 'Clic para editar',
    },
    // User Guide
    userGuide: {
      title: 'Guía Visual de Uso',
      subtitle: 'Edición de Imágenes con IA • Variantes • Área de Trabajo Pro',
      footer: 'DamyxIA - Tu asistente de edición de imágenes con IA',
    },
    // Image Editor
    imageEditor: {
      basicTools: 'Herramientas Básicas',
      adjustments: 'Ajustes',
      aiTools: 'Herramientas IA',
      selectionTools: 'Selección',
      rotate90CW: 'Rotar 90° derecha',
      rotate90CCW: 'Rotar 90° izquierda',
      flipHorizontal: 'Voltear horizontal',
      flipVertical: 'Voltear vertical',
      crop: 'Recortar',
      zoomIn: 'Acercar',
      zoomOut: 'Alejar',
      fitToScreen: 'Ajustar a pantalla',
      brightness: 'Brillo',
      contrast: 'Contraste',
      saturation: 'Saturación',
      clarity: 'Claridad',
      enhanceImage: 'Mejorar imagen',
      changeStyle: 'Cambiar estilo',
      changeColor: 'Cambiar color',
      changeBackground: 'Cambiar fondo',
      sharpen: 'Aumentar nitidez',
      createVariant: 'Crear variante',
      rectangleSelect: 'Selección rectangular',
      lassoSelect: 'Selección lazo',
      eraseSelection: 'Borrar selección',
      fillSelection: 'Rellenar con IA',
      applyChanges: 'Aplicar cambios',
      resetChanges: 'Restablecer',
      undo: 'Deshacer',
      redo: 'Rehacer',
      processing: 'Procesando...',
      enhancing: 'Mejorando imagen...',
      changingStyle: 'Cambiando estilo...',
      creatingVariant: 'Creando variante...',
      painting: 'Pintura',
      sketch: 'Boceto',
      design: 'Diseño',
      artistic: 'Artístico',
      variantCreated: 'Variante creada exitosamente',
      errorCreatingVariant: 'Error al crear variante',
      imageEnhanced: 'Imagen mejorada',
      errorEnhancing: 'Error al mejorar imagen',
      cancelSelection: 'Cancelar selección',
      publishFirst: 'Primero publica la imagen',
      selectAreaFirst: 'Selecciona un área primero',
      whatToGenerate: '¿Qué quieres generar en la región seleccionada?',
      imageCropped: 'Imagen recortada',
    },
    // Message Bubble
    messages: {
      downloadImage: 'Descargar imagen',
      imageGeneratedBy: 'Imagen generada por DamyxIA',
      pdfDownloaded: 'PDF descargado',
      errorGeneratingPdf: 'Error al generar PDF',
      editedCodeExecuted: '¡Código editado ejecutado!',
      codeSentToWorkspace: '¡Código enviado al área de trabajo!',
      goToWorkspace: 'Ve al área de trabajo para ver el código',
      copied: 'Copiado',
      copyMessage: 'Copiar mensaje',
      deleteMessage: 'Borrar mensaje',
      analyzingCode: 'Analizando código...',
      executeInWorkspace: 'Ejecutar código en el área de trabajo',
      sent: '¡Enviado!',
      execute: 'Ejecutar',
      requestingFix: 'Solicitando corrección...',
      askToFix: 'Pedir a DamyxIA que corrija los errores',
      fix: 'Corregir',
      editBeforeExecute: 'Editar código antes de ejecutar',
      edit: 'Editar',
      copyResponse: 'Copiar respuesta',
      copy: 'Copiar',
      saved: 'Guardado',
      saveResponse: 'Salvar respuesta',
      save: 'Salvar',
      downloadAsPdf: 'Descargar como PDF',
      generating: 'Generando...',
      userMessage: 'Usuario',
      aiMessage: 'DamyxIA',
      you: 'Tú',
      thinking: 'Pensando...',
      typing: 'Escribiendo...',
      // Code Editor
      codeEditor: 'Editor de Código',
      modifyAndExecute: '(modifica y ejecuta)',
      resetToOriginal: 'Restaurar código original',
      reset: 'Restaurar',
      closeEditor: 'Cerrar editor',
      close: 'Cerrar',
      writeOrModifyCode: 'Escribe o modifica el código aquí...',
      lines: 'líneas',
      codeModified: '• Código modificado',
      codeCopied: 'Código copiado',
      executed: '¡Ejecutado!',
      executeCode: 'Ejecutar Código',
      responseFrom: 'Respuesta de',
      delete: 'Borrar',
    },
    // Historial Avanzado
    historial: {
      title: 'Historial Avanzado',
      totalImages: 'imagen(es) en total',
      stats: 'Estadisticas',
      exportZip: 'ZIP',
      searchPlaceholder: 'Buscar por prompt...',
      filters: 'Filtros',
      dateFrom: 'Desde',
      dateTo: 'Hasta',
      typeAll: 'Todos',
      typeGeneration: 'Generacion',
      typeBgRemoval: 'Bg Removal',
      typeVariant: 'Variante',
      typeUpscale: 'Upscale',
      typeFilter: 'Filtro IA',
      sortNewest: 'Mas recientes',
      sortOldest: 'Mas antiguas',
      sortPrompt: 'Por prompt (A-Z)',
      clearFilters: 'Limpiar',
      selectAll: 'Seleccionar todas',
      deselectAll: 'Deseleccionar',
      selected: 'seleccionada(s)',
      deleteSelected: 'Eliminar',
      noResults: 'Sin resultados',
      noImages: 'No hay imagenes guardadas',
      noResultsHint: 'Intenta ajustar los filtros de busqueda',
      noImagesHint: 'Las imagenes que guardes desde el generador apareceran aqui',
      totalImagesLabel: 'Total imagenes',
      favorites: 'Favoritas',
      firstImage: 'Primera imagen',
      lastImage: 'Ultima imagen',
      fullView: 'Vista Completa',
      copy: 'Copiar',
      copied: 'Copiada',
      download: 'Descargar',
      delete: 'Eliminar',
      premiumTitle: 'Historial Avanzado',
      premiumDesc: 'Esta funcion es exclusiva para usuarios Premium. Incluye filtros por fecha, tipo, busqueda, favoritos, eliminacion y organizacion avanzada.',
      viewPlans: 'Ver Planes Premium',
      back: 'Volver',
      imageDownloaded: 'Imagen descargada',
      imageDeleted: 'Imagen eliminada',
      imagesDeleted: 'imagen(es) eliminada(s)',
      deleteConfirm: 'Eliminar',
      errorDownload: 'Error al descargar',
      errorDelete: 'Error al eliminar',
      errorCopy: 'Error al copiar',
      errorLoad: 'Error al cargar historial',
      favoriteError: 'Error al cambiar favorito',
      zipExported: 'ZIP exportado correctamente',
      zipError: 'Error al exportar',
      imageCopied: 'Imagen copiada',
    },
    // Pipelines IA
    pipelines: {
      title: 'Pipelines IA',
      count: 'pipeline(s)',
      createPipeline: 'Crear Pipeline',
      noPipelines: 'No hay pipelines',
      noPipelinesHint: 'Crea tu primer pipeline para automatizar flujos de IA',
      newPipeline: 'Nuevo Pipeline',
      name: 'Nombre',
      description: 'Descripcion (opcional)',
      triggerType: 'Tipo de Trigger',
      manual: 'Manual',
      schedule: 'Programado',
      event: 'Evento',
      cronPlaceholder: 'CRON (ej: 0 9 * * *)',
      cancel: 'Cancelar',
      save: 'Guardar',
      saving: 'Guardando...',
      created: 'Pipeline creado',
      deleted: 'Pipeline eliminado',
      run: 'Ejecutar',
      running: 'Ejecutando...',
      executed: 'Pipeline ejecutado correctamente',
      deleteConfirm: 'Eliminar este pipeline y todos sus steps?',
      errorCreate: 'Error al crear',
      errorDelete: 'Error al eliminar',
      errorRun: 'Error al ejecutar pipeline',
      nameRequired: 'Nombre requerido',
      premiumDesc: 'Esta funcion es exclusiva para usuarios Premium.',
      trigger: 'Trigger',
      active: 'activo',
      paused: 'pausado',
    },
    // Automatizaciones IA
    automations: {
      title: 'Automatizaciones IA',
      viewPipelines: 'Ver Pipelines',
      pipeline: 'Pipeline',
      stepsTitle: 'Steps del Pipeline',
      noSteps: 'No hay steps configurados',
      addStep: 'Agregar Step',
      order: 'Orden',
      actionType: 'Tipo de accion',
      generateImage: 'Generar Imagen',
      summarize: 'Resumir Texto',
      classify: 'Clasificar',
      webhook: 'Webhook',
      configJson: 'Config (JSON)',
      configPlaceholder: '{"prompt": "..."}',
      adding: 'Agregando...',
      stepAdded: 'Step agregado',
      stepDeleted: 'Step eliminado',
      invalidJson: 'Config JSON invalido',
      errorAdd: 'Error al agregar',
      errorDelete: 'Error al eliminar',
      noPipelinesMsg: 'No hay pipelines',
      noPipelinesHint: 'Primero crea un pipeline para agregar automatizaciones',
      goToPipelines: 'Ir a Pipelines',
      premiumDesc: 'Esta funcion es exclusiva para usuarios Premium.',
      step: 'Step',
    },
    // Theme
    theme: {
      darkMode: 'Modo Oscuro',
      premiumOnly: 'Premium',
    },
  },
  en: {
    // Welcome Screen
    welcome: {
      title: 'The AI that builds real projects',
      // 🟥 Cuadro Negro 2026-05-30 — keys for alternating title effect.
      titleAlt1: 'Your AI that builds real projects',
      titleAlt2: 'DamyxIA',
      subtitle: 'Your artificial intelligence assistant',
      login: 'Sign In',
      register: 'Sign Up',
      installApp: 'Install DamyxIA',
      appInstalled: 'App installed!',
      poweredBy: 'Powered by advanced AI',
      userRights: 'User Rights',
      forWhom: 'Who is DamyxIA for',
      forWhomTitle: 'Visionaries without code',
      forWhomDesc: 'People with great ideas who should not be limited by lack of technical or economic knowledge.',
      forWhomFeature1: 'Intuitive interface in Spanish and English',
      forWhomFeature2: 'No learning curve, no limits on ideas',
    },
    // 🟥 Home page (/home) — official DamyxIA pitch.
    home: {
      title: 'The AI that builds real projects',
      taglineLine1: 'You write what you want.',
      taglineLine2: 'DamyxIA builds the entire project.',
      intro1: 'DamyxIA is not a chatbot.',
      intro2: 'It is a complete project builder, capable of generating web applications, APIs, CLIs, mobile apps, monorepos and hybrid systems ready to run.',
      intro3: 'You write what you want. DamyxIA builds the entire project.',
      howItWorks: {
        heading: 'How it works',
        s1Title: '1. Understand your idea',
        s1Body: 'Write in natural language what you want to build. DamyxIA analyzes your intent, detects frameworks, structure and the necessary components.',
        s2Title: '2. Generates the full project',
        s2Body: 'Produces real, organized code with folders, modules, dependencies and files ready to run.',
        s3Title: '3. Renders or activates Alternative Mode',
        s3Items: [
          'If the project is web → you see it rendered live.',
          'If it is backend, CLI, mobile or monorepo → DamyxIA activates Alternative Mode, explaining what kind of project it is and how to use it.',
        ],
        s4Title: '4. Explore the generated files',
        s4Intro: 'Open the File Explorer:',
        s4Items: [
          'Real project tree',
          'Folders first, files later',
          'Size per file (KB/MB)',
          'Total files and total weight',
          'Clean, professional, bilingual view',
          '100% read-only',
        ],
        s5Title: '5. Download the professional ZIP',
        s5Intro: 'Every download includes a smart README, with:',
        s5Items: [
          'Project name',
          'Generation date',
          'Detected frameworks',
          'Recommended paths (cd frontend, cd backend, etc.)',
          'Real installation and run instructions',
          'Smart folder tree',
          'Counter of truncated files',
          'Footer "Powered by DamyxIA"',
        ],
        s5Note: 'If the project already had a README → it is not overwritten.',
      },
      canGenerate: {
        heading: 'What DamyxIA can generate',
        frontendTitle: 'Frontend',
        frontendItems: ['Next.js', 'Vite + React', 'Astro', 'Vanilla web apps'],
        backendTitle: 'Backend',
        backendItems: ['Express', 'Fastify', 'Koa', 'Hono', 'Pure Node.js'],
        mobileTitle: 'Mobile',
        mobileItems: ['Expo', 'React Native'],
        cliTitle: 'CLI Tools',
        cliItems: ['Node CLI', 'Utility scripts'],
        monoTitle: 'Hybrid monorepos',
        monoItems: [
          'Next.js + Express',
          'Vite + Fastify',
          'Expo + API Node',
          'Web + CLI',
          'Any multiple combination',
        ],
      },
      altMode: {
        heading: 'Alternative Mode (when it cannot be rendered)',
        intro: 'If the project is not web (backend, CLI, mobile, monorepo), DamyxIA activates a special panel:',
        items: [
          'Explains the project type',
          'Shows how to run it',
          'Lets you view the generated files',
          'Lets you download the professional ZIP',
        ],
        outro: 'No errors. No confusion. No technical noise.',
      },
      aiTemplates: {
        heading: 'AI Templates — Build complete projects in seconds',
        description: 'AI Templates are ready-to-use models that let you generate dashboards, landing pages, funnels, portfolios and more with a single click. Just pick a template and send it to your Workspace to view and edit it visually.',
        accessTitle: 'Access',
        accessFree: 'Free users: can use all free templates.',
        accessPremium: 'Premium users: full access to all templates, including advanced and AI-powered marketing templates.',
        howToTitle: 'How to use them',
        howToItems: [
          'Open the Templates panel from the Workspace.',
          'Choose a template.',
          'Click "Send to Workspace".',
          'The template will open visually, ready to edit.',
        ],
        premiumTitle: 'Premium Templates',
        premiumNote: 'Some advanced templates are marked as "PREMIUM" and require an active subscription.',
      },
      explorer: {
        heading: 'File Explorer (Drawer)',
        intro: 'A professional side panel with:',
        items: [
          'Real project tree',
          'Size per file',
          'Total files and total weight',
          'Close with ESC or click outside',
          'Bilingual ES/EN',
          '100% read-only',
        ],
        outro: 'It is like looking at the project from VSCode, but inside DamyxIA.',
      },
      zip: {
        heading: 'Professional ZIP with smart README',
        intro: 'Each ZIP includes:',
        items: [
          'Bilingual README',
          'Metadata (name + date)',
          'Detected frameworks',
          'Recommended paths',
          'Real instructions',
          'Smart tree',
          'Counter of truncated files',
          'Footer "Powered by DamyxIA"',
        ],
      },
      bilingual: {
        heading: 'Automatic bilingual',
        intro: 'DamyxIA detects your language (ES/EN) and adapts:',
        items: ['Messages', 'Panels', 'README', 'Truncated tree', 'Footer', 'Hybrid sections'],
      },
      stability: {
        heading: 'Stability and security',
        items: [
          'Does not touch the AI engine',
          'Does not touch the Orchestrator',
          'Does not touch the Auto-Builder',
          'Does not touch the main Workspace',
          'Does not execute code',
          'Does not modify existing projects',
        ],
      },
      status: {
        heading: 'Current system status',
        intro: 'DamyxIA is at its best historical state:',
        items: [
          'More than 100 automated tests guarantee stability',
          'Zero console errors',
          'Complete Alternative Mode',
          'Perfect hybrid README',
          'Smart tree',
          'Professional drawer',
          'STANDBY active (REGISTRATION_OPEN=false)',
        ],
      },
      cta: {
        heading: 'Start building',
        line1: 'Ask DamyxIA for whatever you want to create.',
        line2: 'The complete project appears in seconds.',
      },
    },
    // 🟥 GitHub integration (Premium-only — Cuadro Negro 2026-05-30)
    github: {
      saveToGitHub: 'Save to GitHub',
      connectGitHub: 'Connect with GitHub',
      repoCreated: 'Repository created',
      saveError: 'Error saving to GitHub',
    },
    // Features
    features: {
      codeGen: 'Code Generation',
      codeGenDesc: 'Create code in any language',
      imageGen: 'Image Creation',
      imageGenDesc: 'Generate images with AI',
      diagrams: 'Diagrams',
      diagramsDesc: 'Create visual diagrams',
      memory: 'Contextual Memory',
      memoryDesc: 'Remembers your preferences',
      github: 'GitHub',
      githubDesc: 'Full integration',
    },
    // Terms Modal
    terms: {
      title: 'Terms and Conditions',
      intellectualProperty: 'Your Intellectual Property',
      intellectualPropertyText: 'All applications, projects or content generated through our <strong>DamyxIA</strong> platform are considered <strong class="text-green-400">your property</strong>. We do not assume any ownership rights over anything you create here within DamyxIA.',
      totalFreedom: 'Total Freedom',
      totalFreedomText: 'Your ideas and intellectual property will continue to be <strong>yours alone</strong>. You have the freedom to <strong class="text-green-400">use, modify, distribute or sell</strong> the generated applications as permitted by law.',
      yourResponsibility: 'Your Responsibility',
      yourResponsibilityText: 'You are <strong>solely responsible</strong> for your acts or actions. It is also your responsibility to save your work and projects. We assume no responsibility for data loss due to not saving your work.',
      damyxiaRights: 'DamyxIA Rights',
      damyxiaRightsText1: 'DamyxIA reserves the right to introduce at any time <strong>improvements or changes</strong> to any existing agreement with the user.',
      damyxiaRightsText2: 'DamyxIA may <strong class="text-red-400">cease its services without prior notice</strong> for the necessary time, even indefinitely, where everything could cease to exist or function.',
      damyxiaRightsText3: '<strong class="text-green-400">We will never use</strong> user applications or projects for any purpose, neither personal nor any other purpose.',
      damyxiaRightsText4: '⚠️ It is the exclusive responsibility of the user to save and backup outside of DamyxIA all their work and applications, to avoid any unpleasant future situation.',
      disclaimer: 'By using DamyxIA, you accept these terms and conditions.',
      understood: 'Understood',
    },
    // Auth
    auth: {
      login: 'Sign In',
      register: 'Sign Up',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm password',
      forgotPassword: 'Forgot your password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      registerHere: 'Sign up here',
      loginHere: 'Sign in here',
      loggingIn: 'Signing in...',
      registering: 'Registering...',
      backToLogin: 'Back to sign in',
      passwordsNotMatch: 'Passwords do not match',
      passwordMinLength: 'Password must be at least 6 characters',
    },
    // Forgot Password
    forgotPwd: {
      title: 'Recover Password',
      resetTitle: 'Reset Password',
      subtitle: 'Enter your email to receive a recovery link',
      checkEmail: 'Check your email',
      createNew: 'Create a new password',
      done: 'Done! Your password has been updated',
      sendLink: 'Send recovery link',
      sending: 'Sending...',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm password',
      resetting: 'Resetting...',
      resetPassword: 'Reset password',
      successReset: 'Your password has been reset successfully. You can now sign in with your new password.',
      goToLogin: 'Go to Sign In',
      linkReady: 'Ready! Use this link:',
      resetMyPassword: '🔐 Reset My Password',
      copy: 'Copy',
      linkCopied: 'Link copied!',
      alsoSentTo: 'We also sent the link to',
      checkSpam: "(Check spam if you don't see it)",
      tryOtherEmail: '← Try another email',
    },
    // Dashboard
    dashboard: {
      newChat: 'New conversation',
      conversations: 'Conversations',
      noConversations: 'No conversations',
      workspace: 'Workspace',
      internalWorkspace: 'Internal Workspace',
      workspaceTitle: 'Workspace',
      workspaceSubtitle: 'Right Panel • Live View and Execution',
      workspaceEmpty: 'Empty workspace',
      workspaceEmptyDesc: 'Select or create a conversation to see executed work',
      noCodeOrDiagrams: 'No code or diagrams',
      noCodeOrDiagramsDesc: 'Ask DamyxIA to generate code or diagrams',
      poweredBy: 'By DamyxIA',
      damyxiaChoice: 'DamyxIA is Your Choice',
      dragImageHint: 'Drag an image here or type a message',
      livePreview: 'Live Preview',
      previewArea: 'Preview Area',
      previewAreaDesc: 'The result of what DamyxIA generates will appear here. Type something in the left panel to start.',
      rightPanel: 'Right Panel',
      liveExecution: 'Preview and Execution',
      synced: 'Synced',
      updated: 'Updated',
      visualInspection: 'Visual Inspection',
      generatedCode: 'Generated Code',
      footer: 'Right Panel • Preview and Execution • DamyxIA',
      waiting: 'Waiting',
      generating: 'Generating...',
      generated: 'Generated',
      repaired: 'Repaired',
      error: 'Error',
      refreshView: 'Refresh view',
      viewCode: 'View code',
      expand: 'Expand',
      minimize: 'Minimize',
      review: 'Review',
      chat: 'Chat',
      logout: 'Sign Out',
      profile: 'Profile',
      settings: 'Settings',
      connected: 'Connected',
      diagrams: 'diagram(s)',
      codeBlocks: 'code block(s)',
      copied: 'Copied',
      download: 'Download',
      downloadSvg: 'Download SVG',
      diagramDownloaded: 'Diagram downloaded',
      run: 'Run',
      reviewProject: 'Review Project',
      fixesApplied: 'Fixes applied',
      flowchart: 'Flowchart',
      sequence: 'Sequence Diagram',
      classDiagram: 'Class Diagram',
      stateDiagram: 'State Diagram',
      erDiagram: 'ER Diagram',
      gantt: 'Gantt Chart',
      pie: 'Pie Chart',
      gitGraph: 'Git Graph',
      mindmap: 'Mind Map',
      timeline: 'Timeline',
      diagram: 'Diagram',
    },
    // Gallery
    gallery: {
      title: 'Gallery',
      communityGallery: 'Community Gallery',
      shareDesign: 'Share Design',
      likes: 'Likes',
      noDesigns: 'No designs',
      myApps: 'My Apps',
      publicGallery: 'Public Apps',
      publish: 'Publish',
      clone: 'Clone',
      edit: 'Edit',
      delete: 'Delete',
      views: 'Views',
      clones: 'Clones',
      published: 'Published',
      draft: 'Draft',
      archived: 'Archived',
      makePublic: 'Make public',
      makePrivate: 'Make private',
      publishApp: 'Publish my App',
      appName: 'App Name',
      description: 'Description',
      tags: 'Tags',
      public: 'Public',
      private: 'Private',
      publishSuccess: 'App published successfully!',
      cloneSuccess: 'App cloned!',
      noApps: 'No apps',
      createFirst: 'Create your first app with DamyxIA',
      discoverApps: 'Discover apps created by the community',
      loadMore: 'Load more',
      recent: 'Recent',
      popular: 'Popular',
      mostViewed: 'Most viewed',
      allTags: 'All',
      searchApps: 'Search apps...',
    },
    // Floating Preview
    floatingPreview: {
      title: 'Dashboard Editor',
      code: 'Code',
      editCode: 'Edit Code',
      apply: 'Apply',
      discard: 'Discard changes',
      reload: 'Reload',
      openFloating: 'Open in floating window',
      maximize: 'Maximize',
      restore: 'Restore',
    },
    // Chat
    chat: {
      placeholder: 'Type your message to DamyxIA...',
      placeholderFile: 'Ask a question about the file...',
      send: 'Send',
      thinking: 'Thinking...',
      attachFile: 'Attach file',
      generateImage: 'Generate image',
      generateDiagram: 'Generate diagram',
      copy: 'Copy',
      copied: 'Copied!',
      download: 'Download',
      analyzeFile: 'Analyze this file',
      supportedFormats: 'Supports: Code, PDFs, Images • Generate images with AI',
      fileTypeNotSupported: 'File type not supported. Allowed extensions:',
      fileTooLarge: 'File is too large. Maximum 10MB.',
      newConversation: 'New Conversation',
      welcome: 'Welcome to DamyxIA',
      welcomeDesc: 'Your artificial intelligence assistant. How can I help you today?',
      suggestions: {
        code: 'Generate code',
        image: 'Create an image',
        diagram: 'Make a diagram',
        help: 'Help me with...',
      },
      rollback: 'Rollback',
    },
    // App Publishing
    appPublish: {
      published: 'App published!',
      publishedUrl: 'App published! URL:',
      errorPublishing: 'Error publishing app',
      notPublished: 'This image is not published yet. Use the chat to publish it.',
    },
    // Image Generator
    imageGen: {
      title: 'Generate Image',
      prompt: 'Describe the image you want to create',
      promptPlaceholder: 'E.g: A sunset on the beach with palm trees...',
      size: 'Size',
      style: 'Style',
      generate: 'Generate Image',
      generating: 'Generating...',
      styles: {
        vivid: 'Vivid',
        natural: 'Natural',
      },
      actions: {
        copy: 'Copy',
        download: 'Download',
        analyze: 'Analyze',
        variation: 'Variation',
      },
    },
    // Diagram Generator
    diagramGen: {
      title: 'Generate Diagram',
      description: 'Describe the diagram',
      descPlaceholder: 'E.g: User authentication flow...',
      type: 'Diagram type',
      generate: 'Generate Diagram',
      generating: 'Generating...',
      types: {
        flowchart: 'Flowchart',
        sequence: 'Sequence Diagram',
        class: 'Class Diagram',
        er: 'Entity-Relationship',
        state: 'State Diagram',
        pie: 'Pie Chart',
        gantt: 'Gantt Chart',
      },
    },
    // Memory Profile
    memory: {
      title: 'Contextual Memory',
      profileTitle: 'User Profile',
      profilePlaceholder: 'Describe who you are, your role, preferences...',
      contextTitle: 'Global Context',
      contextPlaceholder: 'Information the AI should always remember...',
      save: 'Save',
      saving: 'Saving...',
      saved: 'Saved!',
      cancel: 'Cancel',
    },
    // GitHub
    github: {
      title: 'Connect with GitHub',
      tokenLabel: 'Personal Access Token',
      tokenPlaceholder: 'ghp_xxxxxxxxxxxx',
      tokenHelp: 'Generate a token in GitHub > Settings > Developer settings > Personal access tokens',
      connect: 'Connect',
      connecting: 'Connecting...',
      connected: 'Connected',
      disconnect: 'Disconnect',
      repositories: 'Repositories',
      noRepos: 'No repositories found',
    },
    // Settings Modal
    settings: {
      title: 'Settings',
      subtitle: 'Manage how you receive email notifications',
      projectUpdates: 'Project updates',
      projectUpdatesDesc: 'Get notified when your projects are updated or completed',
      newFeatures: 'New features',
      newFeaturesDesc: 'Be the first to know about new features and improvements',
      communityHighlights: 'Community highlights',
      communityHighlightsDesc: 'Weekly summary of the most popular community projects',
      securityAlerts: 'Security alerts',
      securityAlertsDesc: 'Important security notifications about your account',
      marketing: 'Marketing and promotions',
      marketingDesc: 'Special offers, tips and promotional content',
      savePreferences: 'Save preferences',
      saved: 'Preferences saved!',
    },
    // Logout Modal
    logout: {
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      saveReminder: 'Make sure you have saved your work before leaving.',
      confirm: 'Yes, sign out',
      cancel: 'Cancel',
      saveAndExit: 'Save and Exit',
      exitWithoutSave: 'Exit without Saving',
    },
    // User Profile
    userProfile: {
      title: 'My Profile',
      changePhoto: 'Change photo',
      changePassword: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      save: 'Save changes',
      saving: 'Saving...',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      language: 'Language',
      copyError: 'Could not copy',
      downloadError: 'Download error',
      // Share
      shareTwitter: 'Share on Twitter',
      shareFacebook: 'Share on Facebook',
      shareLinkedIn: 'Share on LinkedIn',
      shareWhatsApp: 'Share on WhatsApp',
      shareEmail: 'Share via Email',
      // Likes
      like: 'Like',
      unlike: 'Unlike',
      // Processing
      processing: 'Processing...',
      pleaseWait: 'Please wait...',
      // Misc
      copied: 'Copied',
      copy: 'Copy',
      download: 'Download',
      refresh: 'Refresh',
      search: 'Search',
      noResults: 'No results',
      seeMore: 'See more',
      seeLess: 'See less',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      and: 'and',
      from: 'from',
      to: 'to',
      at: 'at',
      by: 'by',
    },
    // Internal Workspace
    internalWorkspace: {
      title: 'Internal Workspace',
      subtitle: 'Works created by DamyxIA',
      newWork: 'New Work',
      search: 'Search works...',
      empty: 'No internal works',
      emptyHint: 'Create your first work using the button above',
      loading: 'Loading works...',
      deleteConfirm: 'Delete this work?',
      deleted: 'Work deleted',
      created: 'Work created',
      updated: 'Work updated',
      error: 'Error processing request',
      // New Module Form
      createModule: 'Create Module',
      moduleName: 'Module name',
      moduleDescription: 'Description (optional)',
      moduleType: 'Type',
      moduleContent: 'Code content',
      cancel: 'Cancel',
      create: 'Create',
      nameRequired: 'Name is required',
      // Module Card
      delete: 'Delete',
      doubleClickToEdit: 'Double click to edit',
      noDescription: '(No description)',
      copyFailed: 'Copy failed',
      codeSaved: 'Code saved',
      saveFailed: 'Save failed',
      pdfDownloaded: 'PDF downloaded',
      pdfExportFailed: 'PDF export failed',
      // Module Details
      preview: 'Preview',
      code: 'Code',
      copy: 'Copy',
      save: 'Save',
      exportPdf: 'Export PDF',
      editInChat: 'Edit in chat',
      close: 'Close',
      noModuleSelected: 'Select a module to see more details',
      // Types
      component: 'Component',
      page: 'Page',
      utility: 'Utility',
      style: 'Style',
      config: 'Config',
    },
    // Workspace (Dashboard)
    workspace: {
      title: 'Workspace',
      expand: 'Expand',
      collapse: 'Collapse',
      items: 'item(s)',
      images: 'Images',
      versionRestored: 'Version restored',
      failedToLoad: 'Failed to load image',
      noImageSelected: 'No image selected',
      emptyWorkspace: 'Empty workspace',
      emptyHint: 'Upload an image to chat to get started',
      rollback: 'Rollback',
      history: 'History',
      versions: 'Versions',
      latestVersion: 'Latest version',
    },
    // Image Workspace
    imageWorkspace: {
      save: 'Save',
      download: 'Download',
      print: 'Print',
      edit: 'Edit',
      delete: 'Delete',
      variants: 'Variants',
      original: 'Original',
      images: 'Images',
      closeImage: 'Close image',
      variant: 'Variant',
      noVariants: 'No variants',
      createVariantHint: 'Use the editor to create variants',
      saved: 'Image saved',
      downloaded: 'Image downloaded',
      deleted: 'Item deleted',
      errorSaving: 'Error saving',
      errorDownloading: 'Error downloading',
      errorDeleting: 'Error deleting',
      confirmDelete: 'Delete this item?',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fitToScreen: 'Fit',
      imageSource: 'Source Image',
      imageVariant: 'Variant',
      noImageSelected: 'No image selected',
      dropImageHere: 'Drop image here',
      willAppearInChat: 'It will appear in the conversation',
      processing: 'Processing...',
      dragOrTypeMessage: 'Drag an image here or type a message',
      // Additional
      popupBlocked: 'Popup blocked',
      noImageToDelete: 'No image to delete',
      confirmDeleteImage: 'Are you sure you want to delete this image?',
      imageDeleted: 'Image deleted',
      errorDeleted: 'Error deleting',
      back: 'Back',
      doubleClickToEdit: 'Double-click to edit',
    },
    // Image Gallery Modal
    imageGallery: {
      title: 'Image Gallery',
      allImages: 'All',
      originals: 'Originals',
      variants: 'Variants',
      pending: 'Pending',
      noImages: 'No images',
      noImagesHint: 'Upload an image to chat to get started',
      search: 'Search images...',
      loadingImages: 'Loading images...',
      imageSource: 'Original',
      imageVariant: 'Variant',
      imageReady: 'Ready to work',
      selectToEdit: 'Click to edit',
    },
    // User Guide
    userGuide: {
      title: 'Visual User Guide',
      subtitle: 'AI Image Editing • Variants • Pro Workspace',
      footer: 'DamyxIA - Your AI-powered image editing assistant',
    },
    // Image Editor
    imageEditor: {
      basicTools: 'Basic Tools',
      adjustments: 'Adjustments',
      aiTools: 'AI Tools',
      selectionTools: 'Selection',
      rotate90CW: 'Rotate 90° right',
      rotate90CCW: 'Rotate 90° left',
      flipHorizontal: 'Flip horizontal',
      flipVertical: 'Flip vertical',
      crop: 'Crop',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fitToScreen: 'Fit to screen',
      brightness: 'Brightness',
      contrast: 'Contrast',
      saturation: 'Saturation',
      clarity: 'Clarity',
      enhanceImage: 'Enhance image',
      changeStyle: 'Change style',
      changeColor: 'Change color',
      changeBackground: 'Change background',
      sharpen: 'Sharpen',
      createVariant: 'Create variant',
      rectangleSelect: 'Rectangle select',
      lassoSelect: 'Lasso select',
      eraseSelection: 'Erase selection',
      fillSelection: 'Fill with AI',
      applyChanges: 'Apply changes',
      resetChanges: 'Reset',
      undo: 'Undo',
      redo: 'Redo',
      processing: 'Processing...',
      enhancing: 'Enhancing image...',
      changingStyle: 'Changing style...',
      creatingVariant: 'Creating variant...',
      painting: 'Painting',
      sketch: 'Sketch',
      design: 'Design',
      artistic: 'Artistic',
      variantCreated: 'Variant created successfully',
      errorCreatingVariant: 'Error creating variant',
      imageEnhanced: 'Image enhanced',
      errorEnhancing: 'Error enhancing image',
      cancelSelection: 'Cancel selection',
      publishFirst: 'Publish the image first',
      selectAreaFirst: 'Select an area first',
      whatToGenerate: 'What do you want to generate in the selected region?',
      imageCropped: 'Image cropped',
    },
    // Message Bubble
    messages: {
      downloadImage: 'Download image',
      imageGeneratedBy: 'Image generated by DamyxIA',
      pdfDownloaded: 'PDF downloaded',
      errorGeneratingPdf: 'Error generating PDF',
      editedCodeExecuted: 'Edited code executed!',
      codeSentToWorkspace: 'Code sent to workspace!',
      goToWorkspace: 'Go to workspace to see the code',
      copied: 'Copied',
      copyMessage: 'Copy message',
      deleteMessage: 'Delete message',
      analyzingCode: 'Analyzing code...',
      executeInWorkspace: 'Execute code in workspace',
      sent: 'Sent!',
      execute: 'Execute',
      requestingFix: 'Requesting fix...',
      askToFix: 'Ask DamyxIA to fix errors',
      fix: 'Fix',
      editBeforeExecute: 'Edit code before executing',
      edit: 'Edit',
      copyResponse: 'Copy response',
      copy: 'Copy',
      saved: 'Saved',
      saveResponse: 'Save response',
      save: 'Save',
      downloadAsPdf: 'Download as PDF',
      generating: 'Generating...',
      userMessage: 'User',
      aiMessage: 'DamyxIA',
      you: 'You',
      thinking: 'Thinking...',
      typing: 'Typing...',
      // Code Editor
      codeEditor: 'Code Editor',
      modifyAndExecute: '(modify and execute)',
      resetToOriginal: 'Reset to original code',
      reset: 'Reset',
      closeEditor: 'Close editor',
      close: 'Close',
      writeOrModifyCode: 'Write or modify code here...',
      lines: 'lines',
      codeModified: '• Code modified',
      codeCopied: 'Code copied',
      executed: 'Executed!',
      executeCode: 'Execute Code',
      responseFrom: 'Response from',
      delete: 'Delete',
    },
    // Historial Avanzado
    historial: {
      title: 'Advanced History',
      totalImages: 'image(s) total',
      stats: 'Statistics',
      exportZip: 'ZIP',
      searchPlaceholder: 'Search by prompt...',
      filters: 'Filters',
      dateFrom: 'From',
      dateTo: 'To',
      typeAll: 'All',
      typeGeneration: 'Generation',
      typeBgRemoval: 'Bg Removal',
      typeVariant: 'Variant',
      typeUpscale: 'Upscale',
      typeFilter: 'AI Filter',
      sortNewest: 'Newest first',
      sortOldest: 'Oldest first',
      sortPrompt: 'By prompt (A-Z)',
      clearFilters: 'Clear',
      selectAll: 'Select all',
      deselectAll: 'Deselect',
      selected: 'selected',
      deleteSelected: 'Delete',
      noResults: 'No results',
      noImages: 'No saved images',
      noResultsHint: 'Try adjusting your search filters',
      noImagesHint: 'Images you save from the generator will appear here',
      totalImagesLabel: 'Total images',
      favorites: 'Favorites',
      firstImage: 'First image',
      lastImage: 'Last image',
      fullView: 'Full View',
      copy: 'Copy',
      copied: 'Copied',
      download: 'Download',
      delete: 'Delete',
      premiumTitle: 'Advanced History',
      premiumDesc: 'This feature is exclusive to Premium users. Includes date filters, type filters, search, favorites, deletion and advanced organization.',
      viewPlans: 'View Premium Plans',
      back: 'Back',
      imageDownloaded: 'Image downloaded',
      imageDeleted: 'Image deleted',
      imagesDeleted: 'image(s) deleted',
      deleteConfirm: 'Delete',
      errorDownload: 'Download error',
      errorDelete: 'Delete error',
      errorCopy: 'Copy error',
      errorLoad: 'Error loading history',
      favoriteError: 'Error changing favorite',
      zipExported: 'ZIP exported successfully',
      zipError: 'Export error',
      imageCopied: 'Image copied',
    },
    // Pipelines IA
    pipelines: {
      title: 'AI Pipelines',
      count: 'pipeline(s)',
      createPipeline: 'Create Pipeline',
      noPipelines: 'No pipelines',
      noPipelinesHint: 'Create your first pipeline to automate AI workflows',
      newPipeline: 'New Pipeline',
      name: 'Name',
      description: 'Description (optional)',
      triggerType: 'Trigger Type',
      manual: 'Manual',
      schedule: 'Schedule',
      event: 'Event',
      cronPlaceholder: 'CRON (e.g: 0 9 * * *)',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
      created: 'Pipeline created',
      deleted: 'Pipeline deleted',
      run: 'Run',
      running: 'Running...',
      executed: 'Pipeline executed successfully',
      deleteConfirm: 'Delete this pipeline and all its steps?',
      errorCreate: 'Error creating',
      errorDelete: 'Error deleting',
      errorRun: 'Error running pipeline',
      nameRequired: 'Name required',
      premiumDesc: 'This feature is exclusive to Premium users.',
      trigger: 'Trigger',
      active: 'active',
      paused: 'paused',
    },
    // Automatizaciones IA
    automations: {
      title: 'AI Automations',
      viewPipelines: 'View Pipelines',
      pipeline: 'Pipeline',
      stepsTitle: 'Pipeline Steps',
      noSteps: 'No steps configured',
      addStep: 'Add Step',
      order: 'Order',
      actionType: 'Action type',
      generateImage: 'Generate Image',
      summarize: 'Summarize Text',
      classify: 'Classify',
      webhook: 'Webhook',
      configJson: 'Config (JSON)',
      configPlaceholder: '{"prompt": "..."}',
      adding: 'Adding...',
      stepAdded: 'Step added',
      stepDeleted: 'Step deleted',
      invalidJson: 'Invalid JSON config',
      errorAdd: 'Error adding',
      errorDelete: 'Error deleting',
      noPipelinesMsg: 'No pipelines',
      noPipelinesHint: 'First create a pipeline to add automations',
      goToPipelines: 'Go to Pipelines',
      premiumDesc: 'This feature is exclusive to Premium users.',
      step: 'Step',
    },
    // Theme
    theme: {
      darkMode: 'Dark Mode',
      premiumOnly: 'Premium',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  // Track if the initial language was set from URL param
  const [isFromUrlParam, setIsFromUrlParam] = useState(false);
  
  const [language, setLanguageState] = useState(() => {
    // Priority 1: Check URL params (for referral links or shared links)
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang)) {
      // URL param takes precedence for display, but we don't save it
      return urlLang;
    }

    // Priority 2: Cookie firmada del backend (cross-device, persiste entre sesiones)
    const cookieLang = readCookie(LANGUAGE_COOKIE_NAME);
    if (cookieLang && SUPPORTED_LANGUAGES.includes(cookieLang)) {
      return cookieLang;
    }

    // Priority 3: Check localStorage (fallback local de hidratacion)
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
      return saved;
    }

    // Priority 4: Detect browser language (auto-detect for new users)
    const browserLang = detectBrowserLanguage();
    return browserLang;
  });

  // Check if initial load was from URL param
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang)) {
      setIsFromUrlParam(true);
    }
  }, []);

  // Sync language preference to backend for logged-in users
  const syncLanguageToBackend = useCallback(async (lang) => {
    const token = getToken();
    if (!token || token === 'guest') return;
    
    try {
      await fetch(`${API_URL}/users/language`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ language: lang })
      });
    } catch (error) {
      // Silently fail - localStorage is the primary storage
      console.warn('Failed to sync language to backend:', error);
    }
  }, []);

  // Load language preference from backend on login
  const loadLanguageFromBackend = useCallback(async () => {
    const token = getToken();
    if (!token || token === 'guest') return;
    
    // Don't override if language was set from URL param
    if (isFromUrlParam) return;
    
    try {
      const response = await fetch(`${API_URL}/users/language`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.language && SUPPORTED_LANGUAGES.includes(data.language)) {
          // Only update if different from current and user has a saved preference
          const currentLocal = localStorage.getItem(LANGUAGE_STORAGE_KEY);
          if (!currentLocal && data.language !== language) {
            setLanguageState(data.language);
            localStorage.setItem(LANGUAGE_STORAGE_KEY, data.language);
          }
        }
      }
    } catch (error) {
      // Silently fail - localStorage is the fallback
      console.warn('Failed to load language from backend:', error);
    }
  }, [language, isFromUrlParam]);

  // Load from backend when component mounts and user is logged in
  useEffect(() => {
    loadLanguageFromBackend();
  }, [loadLanguageFromBackend]);

  // Custom setLanguage that persists to cookie + localStorage and syncs to backend
  // This should ONLY be called from explicit user action (LanguageSelector)
  const setLanguage = useCallback((newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) return;

    setLanguageState(newLang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
    setIsFromUrlParam(false); // User manually changed, so clear URL param flag

    // Cookie firmada del backend (cross-device, anonimo + autenticado)
    syncLanguageCookie(newLang);
    // Perfil de usuario logueado (legado)
    syncLanguageToBackend(newLang);
  }, [syncLanguageToBackend]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'es' ? 'en' : 'es';
    setLanguage(newLang);
  }, [language, setLanguage]);

  // Helper to generate URL with language param
  const getUrlWithLanguage = useCallback((baseUrl) => {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('lang', language);
    return url.toString();
  }, [language]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t,
      getUrlWithLanguage,
      isFromUrlParam
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
